import { useCallback, useEffect, useState } from 'react';

import type { MusicGenre, TrackPlatform } from '../types';
import { useMusicGenreOptions } from '../lib/music-genres';

const exhibitionPlatforms: Array<[TrackPlatform, string]> = [
  ['spotify', 'Spotify'],
  ['youtube', 'YouTube / YouTube Music'],
  ['apple_music', 'Apple Music'],
  ['soundcloud', 'SoundCloud'],
  ['deezer', 'Deezer'],
  ['tidal', 'Tidal'],
];

const emptyExhibition = {
  title: '',
  description: '',
  genre: 'reggaeton' as MusicGenre,
  durationHours: 72,
  challengerArtistName: '',
  challengerPlatform: 'youtube' as TrackPlatform,
  challengerTrackUrl: '',
  opponentArtistName: '',
  opponentPlatform: 'youtube' as TrackPlatform,
  opponentTrackUrl: '',
};

const exhibitionDraftStorageKey = 'agam:admin-exhibition-draft:v1';

function loadExhibitionDraft(): typeof emptyExhibition {
  if (typeof window === 'undefined') return emptyExhibition;
  try {
    const saved = window.localStorage.getItem(exhibitionDraftStorageKey);
    if (!saved) return emptyExhibition;
    return { ...emptyExhibition, ...(JSON.parse(saved) as Partial<typeof emptyExhibition>) };
  } catch {
    return emptyExhibition;
  }
}

type Overview = {
  roles: string[];
  counters: Record<string, number>;
};
type ModerationCase = {
  id: string;
  case_type: string;
  entity_type: string;
  entity_id: string;
  priority: string;
  summary: string;
  created_at: number;
};
type Appeal = {
  id: string;
  email: string;
  public_name: string | null;
  artistic_name: string | null;
  sanction_type: string;
  sanction_reason: string;
  message: string;
};
type UserResult = {
  id: string;
  email: string;
  public_name: string | null;
  artistic_name: string | null;
  country_code: string | null;
  active_sanctions: number;
};
type AuditEvent = {
  id: string;
  actor_role: string;
  action: string;
  entity_type: string;
  reason: string | null;
  created_at: number;
};

const labels: Record<string, string> = {
  openCases: 'Casos abiertos',
  openIntegrityFlags: 'Alertas de votos',
  pendingAppeals: 'Apelaciones',
  pendingPrizes: 'Premios pendientes',
  activeCampaigns: 'Campañas activas',
  activeBattles: 'Batallas activas',
  users: 'Usuarios',
};

export function AdminTrustCenter({ authenticated }: { authenticated: boolean }) {
  const genres = useMusicGenreOptions();
  const [overview, setOverview] = useState<Overview | null>(null);
  const [cases, setCases] = useState<ModerationCase[]>([]);
  const [appeals, setAppeals] = useState<Appeal[]>([]);
  const [audit, setAudit] = useState<AuditEvent[]>([]);
  const [users, setUsers] = useState<UserResult[]>([]);
  const [query, setQuery] = useState('');
  const [message, setMessage] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [genre, setGenre] = useState({ code: '', name: '', context: '' });
  const [role, setRole] = useState({ email: '', role: 'moderator' });
  const [exhibition, setExhibition] = useState(loadExhibitionDraft);

  useEffect(() => {
    window.localStorage.setItem(exhibitionDraftStorageKey, JSON.stringify(exhibition));
  }, [exhibition]);

  const load = useCallback(async () => {
    if (!authenticated) return;
    const response = await fetch('/api/v1/admin/overview', { cache: 'no-store' });
    if (response.status === 403) return setOverview(null);
    if (!response.ok) return;
    setOverview((await response.json()) as Overview);
    const [caseResponse, appealResponse, auditResponse] = await Promise.all([
      fetch('/api/v1/admin/moderation/cases?status=open', { cache: 'no-store' }),
      fetch('/api/v1/admin/appeals?status=pending', { cache: 'no-store' }),
      fetch('/api/v1/admin/audit', { cache: 'no-store' }),
    ]);
    if (caseResponse.ok)
      setCases(((await caseResponse.json()) as { cases: ModerationCase[] }).cases);
    if (appealResponse.ok)
      setAppeals(((await appealResponse.json()) as { appeals: Appeal[] }).appeals);
    if (auditResponse.ok)
      setAudit(((await auditResponse.json()) as { events: AuditEvent[] }).events.slice(0, 12));
  }, [authenticated]);

  useEffect(() => {
    void load();
  }, [load]);

  async function request(url: string, method: 'POST' | 'PATCH', body: unknown) {
    setBusy(true);
    setMessage(null);
    try {
      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const result = (await response.json()) as { detail?: string };
      if (!response.ok) throw new Error(result.detail ?? 'No se pudo completar la acción.');
      await load();
      return true;
    } catch (error) {
      setMessage(error instanceof Error ? error.message : 'No se pudo completar la acción.');
      return false;
    } finally {
      setBusy(false);
    }
  }

  async function searchUsers() {
    if (query.trim().length < 2) return setMessage('Escribe al menos dos caracteres.');
    const response = await fetch(`/api/v1/admin/users?q=${encodeURIComponent(query.trim())}`);
    if (response.ok) setUsers(((await response.json()) as { users: UserResult[] }).users);
  }

  async function decideCase(item: ModerationCase, decision: string) {
    const reason = window.prompt('Motivo de la decisión (mínimo 8 caracteres):');
    if (!reason) return;
    if (item.entity_type === 'battle_comment') {
      await request(`/api/v1/admin/comments/${item.entity_id}/decision`, 'PATCH', {
        decision,
        reason,
      });
    } else if (item.entity_type === 'battle') {
      await request(`/api/v1/admin/battles/${item.entity_id}/review`, 'PATCH', {
        decision: decision === 'restore' ? 'confirm' : 'dismiss_flags',
        reason,
      });
    }
  }

  if (!overview) return null;

  return (
    <section id="centro-confianza" className="mt-24 lg:mt-32">
      <p className="eyebrow">Sprint 10 · Centro de confianza</p>
      <div className="mt-3 flex flex-wrap items-end justify-between gap-4">
        <div>
          <h2 className="text-3xl font-black text-white sm:text-5xl">
            Operación con trazabilidad.
          </h2>
          <p className="mt-3 max-w-3xl text-sm leading-6 text-zinc-400">
            Moderación humana, alertas preventivas y decisiones administrativas registradas.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          {overview.roles.map((item) => (
            <span
              key={item}
              className="rounded-full border border-fuchsia-300/20 bg-fuchsia-300/5 px-3 py-1 text-xs text-fuchsia-100"
            >
              {item}
            </span>
          ))}
        </div>
      </div>

      {message && (
        <p
          role="status"
          className="mt-5 rounded-xl border border-white/10 bg-white/5 px-4 py-3 text-sm text-zinc-300"
        >
          {message}
        </p>
      )}

      <div className="mt-7 grid gap-3 sm:grid-cols-2 xl:grid-cols-7">
        {Object.entries(overview.counters).map(([key, value]) => (
          <article key={key} className="rounded-2xl border border-white/8 bg-white/[0.03] p-4">
            <strong className="block text-2xl text-white">{value.toLocaleString()}</strong>
            <span className="text-[0.68rem] text-zinc-500">{labels[key] ?? key}</span>
          </article>
        ))}
      </div>

      <form
        className="glass-panel mt-6 rounded-[2rem] p-6 sm:p-8"
        onSubmit={async (event) => {
          event.preventDefault();
          const ok = await request('/api/v1/admin/battles/exhibitions', 'POST', exhibition);
          if (ok) {
            window.localStorage.removeItem(exhibitionDraftStorageKey);
            setExhibition(emptyExhibition);
            setMessage('Batalla de exhibición publicada en la arena.');
            window.dispatchEvent(new CustomEvent('agam:battle-created'));
          }
        }}
      >
        <div>
          <p className="text-xs font-black uppercase tracking-[0.2em] text-amber-200/70">
            Exhibiciones editoriales
          </p>
          <h3 className="mt-2 text-xl font-black text-white">
            Crear batalla de canciones populares
          </h3>
          <p className="mt-2 text-sm leading-6 text-zinc-500">
            Se mostrará como una batalla normal, identificada como creación administrativa y sin
            impacto en el ranking ELO.
          </p>
        </div>

        <div className="mt-6 grid gap-4 sm:grid-cols-2">
          <label className="field-label sm:col-span-2">
            Título de la batalla
            <input
              required
              minLength={5}
              maxLength={100}
              className="field-input mt-2"
              placeholder="Ej. Mayor Que Yo 3 vs. Mayor Que Yo"
              value={exhibition.title}
              onChange={(event) => setExhibition({ ...exhibition, title: event.target.value })}
            />
          </label>
          <label className="field-label">
            Género
            <select
              className="field-input mt-2"
              value={exhibition.genre}
              onChange={(event) =>
                setExhibition({ ...exhibition, genre: event.target.value as MusicGenre })
              }
            >
              {genres.map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
          </label>
          <label className="field-label">
            Duración de la votación
            <select
              className="field-input mt-2"
              value={exhibition.durationHours}
              onChange={(event) =>
                setExhibition({ ...exhibition, durationHours: Number(event.target.value) })
              }
            >
              <option value={24}>24 horas</option>
              <option value={48}>48 horas</option>
              <option value={72}>72 horas</option>
              <option value={168}>7 días</option>
            </select>
          </label>
          <ExhibitionEntryFields
            label="Primera canción"
            artistName={exhibition.challengerArtistName}
            platform={exhibition.challengerPlatform}
            trackUrl={exhibition.challengerTrackUrl}
            onArtistName={(value) => setExhibition({ ...exhibition, challengerArtistName: value })}
            onPlatform={(value) => setExhibition({ ...exhibition, challengerPlatform: value })}
            onTrackUrl={(value) => setExhibition({ ...exhibition, challengerTrackUrl: value })}
          />
          <ExhibitionEntryFields
            label="Segunda canción"
            artistName={exhibition.opponentArtistName}
            platform={exhibition.opponentPlatform}
            trackUrl={exhibition.opponentTrackUrl}
            onArtistName={(value) => setExhibition({ ...exhibition, opponentArtistName: value })}
            onPlatform={(value) => setExhibition({ ...exhibition, opponentPlatform: value })}
            onTrackUrl={(value) => setExhibition({ ...exhibition, opponentTrackUrl: value })}
          />
          <label className="field-label sm:col-span-2">
            Descripción editorial (opcional)
            <textarea
              maxLength={500}
              className="field-input mt-2 min-h-24 resize-y"
              placeholder="Explica el contexto de la comparación sin afirmar afiliación oficial."
              value={exhibition.description}
              onChange={(event) =>
                setExhibition({ ...exhibition, description: event.target.value })
              }
            />
          </label>
        </div>
        <div className="mt-5 rounded-2xl border border-amber-200/10 bg-amber-200/[0.03] px-4 py-3 text-xs leading-5 text-amber-100/60">
          Usa únicamente enlaces oficiales. La tarjeta pública indicará “Batalla de exhibición
          creada por el admin” y “Esto no tiene ranking ELO”.
        </div>
        <button
          disabled={busy}
          className="mt-5 w-full rounded-full bg-amber-200 px-5 py-3 text-sm font-black text-zinc-950 disabled:opacity-50"
        >
          {busy ? 'Publicando…' : 'Publicar batalla de exhibición'}
        </button>
      </form>

      <div className="mt-6 grid gap-6 xl:grid-cols-2">
        <div className="glass-panel rounded-[2rem] p-6">
          <h3 className="text-lg font-bold text-white">Cola de moderación</h3>
          <div className="mt-4 space-y-3">
            {cases.length === 0 && <p className="text-sm text-zinc-500">No hay casos abiertos.</p>}
            {cases.slice(0, 8).map((item) => (
              <article key={item.id} className="rounded-2xl border border-white/8 p-4">
                <div className="flex justify-between gap-4 text-xs text-zinc-500">
                  <span>
                    {item.case_type} · {item.priority}
                  </span>
                  <time>{new Date(item.created_at).toLocaleDateString('es')}</time>
                </div>
                <p className="mt-2 text-sm text-zinc-300">{item.summary}</p>
                <div className="mt-3 flex gap-2">
                  <button
                    disabled={busy}
                    onClick={() => void decideCase(item, 'restore')}
                    className="rounded-full bg-emerald-200 px-3 py-1.5 text-xs font-bold text-zinc-950"
                  >
                    Confirmar
                  </button>
                  <button
                    disabled={busy}
                    onClick={() => void decideCase(item, 'hide')}
                    className="rounded-full border border-white/10 px-3 py-1.5 text-xs text-zinc-300"
                  >
                    Ocultar/descartar
                  </button>
                </div>
              </article>
            ))}
          </div>
        </div>

        <div className="glass-panel rounded-[2rem] p-6">
          <h3 className="text-lg font-bold text-white">Apelaciones pendientes</h3>
          <div className="mt-4 space-y-3">
            {appeals.length === 0 && (
              <p className="text-sm text-zinc-500">No hay apelaciones pendientes.</p>
            )}
            {appeals.slice(0, 8).map((item) => (
              <article key={item.id} className="rounded-2xl border border-white/8 p-4 text-sm">
                <strong className="text-white">
                  {item.artistic_name || item.public_name || item.email}
                </strong>
                <p className="mt-1 text-xs text-amber-100">
                  {item.sanction_type}: {item.sanction_reason}
                </p>
                <p className="mt-2 text-zinc-400">{item.message}</p>
                <div className="mt-3 flex gap-2">
                  {(['accepted', 'rejected'] as const).map((decision) => (
                    <button
                      key={decision}
                      disabled={busy}
                      onClick={() => {
                        const reason = window.prompt(
                          'Motivo de la decisión (mínimo 8 caracteres):',
                        );
                        if (reason)
                          void request(`/api/v1/admin/appeals/${item.id}/decision`, 'PATCH', {
                            decision,
                            reason,
                          });
                      }}
                      className="rounded-full border border-white/10 px-3 py-1.5 text-xs text-zinc-300"
                    >
                      {decision === 'accepted' ? 'Aceptar' : 'Rechazar'}
                    </button>
                  ))}
                </div>
              </article>
            ))}
          </div>
        </div>
      </div>

      <div className="mt-6 grid gap-6 xl:grid-cols-3">
        <form
          className="glass-panel rounded-[2rem] p-6"
          onSubmit={async (event) => {
            event.preventDefault();
            const ok = await request('/api/v1/admin/genres', 'POST', {
              ...genre,
              context: genre.context || null,
              reason: 'Ampliación editorial del catálogo musical.',
            });
            if (ok) {
              setGenre({ code: '', name: '', context: '' });
              setMessage('Género añadido al catálogo.');
            }
          }}
        >
          <h3 className="font-bold text-white">Añadir género musical</h3>
          <input
            required
            className="field-input mt-4"
            placeholder="Código: son_nordico"
            value={genre.code}
            onChange={(e) =>
              setGenre({ ...genre, code: e.target.value.toLowerCase().replace(/\s+/g, '_') })
            }
          />
          <input
            required
            className="field-input mt-3"
            placeholder="Nombre público"
            value={genre.name}
            onChange={(e) => setGenre({ ...genre, name: e.target.value })}
          />
          <textarea
            className="field-input mt-3 min-h-20"
            placeholder="Contexto opcional"
            value={genre.context}
            onChange={(e) => setGenre({ ...genre, context: e.target.value })}
          />
          <button
            disabled={busy}
            className="mt-4 w-full rounded-full bg-white px-4 py-2.5 text-sm font-bold text-zinc-950"
          >
            Añadir
          </button>
        </form>

        <form
          className="glass-panel rounded-[2rem] p-6"
          onSubmit={async (event) => {
            event.preventDefault();
            const ok = await request('/api/v1/admin/roles', 'POST', {
              ...role,
              reason: 'Asignación operativa autorizada por administración.',
            });
            if (ok) {
              setRole({ ...role, email: '' });
              setMessage('Rol administrativo asignado.');
            }
          }}
        >
          <h3 className="font-bold text-white">Asignar responsabilidad</h3>
          <input
            required
            type="email"
            className="field-input mt-4"
            placeholder="correo@equipo.com"
            value={role.email}
            onChange={(e) => setRole({ ...role, email: e.target.value })}
          />
          <select
            className="field-input mt-3"
            value={role.role}
            onChange={(e) => setRole({ ...role, role: e.target.value })}
          >
            <option value="moderator">Moderación</option>
            <option value="cup_manager">Copas</option>
            <option value="commercial">Comercial</option>
            <option value="support">Soporte</option>
            <option value="admin">Administración</option>
          </select>
          <button
            disabled={busy}
            className="mt-4 w-full rounded-full bg-fuchsia-200 px-4 py-2.5 text-sm font-bold text-zinc-950"
          >
            Asignar rol
          </button>
        </form>

        <div className="glass-panel rounded-[2rem] p-6">
          <h3 className="font-bold text-white">Buscar y sancionar</h3>
          <div className="mt-4 flex gap-2">
            <input
              className="field-input"
              placeholder="Correo o nombre"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
            <button
              type="button"
              onClick={() => void searchUsers()}
              className="rounded-full bg-white px-4 text-sm font-bold text-zinc-950"
            >
              Buscar
            </button>
          </div>
          <div className="mt-3 space-y-2">
            {users.map((item) => (
              <div
                key={item.id}
                className="rounded-xl border border-white/8 p-3 text-xs text-zinc-400"
              >
                <strong className="block text-white">
                  {item.artistic_name || item.public_name || item.email}
                </strong>
                {item.email} · {item.country_code || 'sin país'}
                <button
                  type="button"
                  onClick={() => {
                    const reason = window.prompt('Motivo de la advertencia (mínimo 8 caracteres):');
                    if (reason)
                      void request(`/api/v1/admin/users/${item.id}/sanctions`, 'POST', {
                        type: 'warning',
                        reason,
                      });
                  }}
                  className="mt-2 block rounded-full border border-amber-200/20 px-3 py-1 text-amber-100"
                >
                  Advertir
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="mt-6 rounded-[2rem] border border-white/8 p-6">
        <h3 className="font-bold text-white">Últimas acciones auditadas</h3>
        <div className="mt-4 divide-y divide-white/7">
          {audit.length === 0 && (
            <p className="text-sm text-zinc-500">Todavía no hay acciones administrativas.</p>
          )}
          {audit.map((item) => (
            <div
              key={item.id}
              className="grid gap-1 py-3 text-xs text-zinc-500 sm:grid-cols-[1fr_1fr_2fr_auto]"
            >
              <span className="text-zinc-300">{item.action}</span>
              <span>{item.entity_type}</span>
              <span>{item.reason || 'Sin motivo adicional'}</span>
              <time>{new Date(item.created_at).toLocaleString('es')}</time>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function ExhibitionEntryFields({
  label,
  artistName,
  platform,
  trackUrl,
  onArtistName,
  onPlatform,
  onTrackUrl,
}: {
  label: string;
  artistName: string;
  platform: TrackPlatform;
  trackUrl: string;
  onArtistName: (value: string) => void;
  onPlatform: (value: TrackPlatform) => void;
  onTrackUrl: (value: string) => void;
}) {
  return (
    <fieldset className="rounded-2xl border border-white/8 p-4">
      <legend className="px-2 text-sm font-bold text-white">{label}</legend>
      <label className="field-label">
        Artista o intérpretes
        <input
          required
          minLength={2}
          maxLength={80}
          className="field-input mt-2"
          placeholder="Nombre mostrado públicamente"
          value={artistName}
          onChange={(event) => onArtistName(event.target.value)}
        />
      </label>
      <label className="field-label mt-3">
        Plataforma
        <select
          className="field-input mt-2"
          value={platform}
          onChange={(event) => onPlatform(event.target.value as TrackPlatform)}
        >
          {exhibitionPlatforms.map(([value, text]) => (
            <option key={value} value={value}>
              {text}
            </option>
          ))}
        </select>
      </label>
      <label className="field-label mt-3">
        Enlace oficial
        <input
          required
          type="url"
          className="field-input mt-2"
          placeholder="https://..."
          value={trackUrl}
          onChange={(event) => onTrackUrl(event.target.value)}
        />
      </label>
    </fieldset>
  );
}
